import _ from "lodash";
import moment from "moment";
import "moment/locale/es";
import { Metric } from "../../../cores/analytics/interface/api/get-insights";
import { InstagramPost } from "../../../cores/analytics/interface/api/get-instagram-feed-posts";
import {
  FollowerBalanceDay,
  InstagramAnalyticsSnapshot,
  InstagramAudienceSegment,
  InstagramHashtagRow,
  InstagramStatItem,
  InstagramTopContent,
  InstagramTrendPoint,
} from "./instagramAnalyticsMock";
import { instagramAnalyticsMock } from "./instagramAnalyticsMock";

const pickMetric = (
  entities: Record<string, Metric>,
  names: string[]
): Metric | undefined => {
  for (const name of names) {
    const m = entities[name];
    if (
      m &&
      ((m.values && m.values.length > 0) ||
        typeof m.total?.value === "number")
    )
      return m;
  }
  return undefined;
};

const totalMetricValue = (m?: Metric): number => {
  if (!m) return 0;
  if (typeof m.total?.value === "number") return m.total.value;
  if (m.values?.length) {
    return m.values.reduce((acc, v) => acc + (Number(v.value) || 0), 0);
  }
  return 0;
};

const dateKey = (label: string): string | null => {
  if (!label?.trim()) return null;

  const iso = moment(label, "YYYY-MM-DD", true);
  if (iso.isValid()) return iso.format("YYYY-MM-DD");

  const common = [
    "MM/DD/YY",
    "MM/DD/YYYY",
    "DD/MM/YY",
    "DD/MM/YYYY",
    "MMMM D, YYYY",
    "dddd, MMMM Do YYYY",
    "MMMM Do YYYY",
  ];
  for (const fmt of common) {
    const m = moment(label, fmt, true);
    if (m.isValid()) return m.format("YYYY-MM-DD");
  }

  const withoutOrdinal = label.replace(/(\d+)(st|nd|rd|th)\b/gi, "$1");
  const looseOrdinal = moment(withoutOrdinal);
  if (looseOrdinal.isValid()) return looseOrdinal.format("YYYY-MM-DD");

  const loose = moment(label);
  return loose.isValid() ? loose.format("YYYY-MM-DD") : null;
};

const formatAxisLabelEs = (key: string): string =>
  moment(key, "YYYY-MM-DD").locale("es").format("D MMM");

const valuesToDayMap = (m?: Metric): Map<string, number> => {
  const map = new Map<string, number>();
  if (!m?.values) return map;
  for (const { label, value } of m.values) {
    const k = dateKey(label);
    if (k) map.set(k, Number(value) || 0);
  }
  return map;
};

const regionNameEs = (code: string): string => {
  const c = code.trim().toUpperCase();
  if (c.length !== 2) return code.trim();
  try {
    return new Intl.DisplayNames(["es"], { type: "region" }).of(c) ?? code;
  } catch {
    return code;
  }
};

const mapDemographicSlices = (
  m: Metric | undefined
): InstagramAudienceSegment[] => {
  if (!m?.values?.length) return [];
  const sum =
    m.values.reduce((a, v) => a + (Number(v.value) || 0), 0) || 1;
  return m.values.map((v) => ({
    label: v.label.replace(/\./g, " · "),
    value: Number(v.value) || 0,
    percentage: Math.round(((Number(v.value) || 0) * 1000) / sum) / 10,
  }));
};

const mapCountrySlices = (m: Metric | undefined): InstagramAudienceSegment[] => {
  if (!m?.values?.length) return [];
  const sum =
    m.values.reduce((a, v) => a + (Number(v.value) || 0), 0) || 1;
  return m.values.map((v) => ({
    label: regionNameEs(v.label),
    value: Number(v.value) || 0,
    percentage: Math.round(((Number(v.value) || 0) * 1000) / sum) / 10,
  }));
};

const buildTrend = (entities: Record<string, Metric>): InstagramTrendPoint[] => {
  const impressions = pickMetric(entities, ["impressions", "views"]);
  const reach = pickMetric(entities, ["reach"]);
  const profileViews = pickMetric(entities, ["profile_views"]);
  const websiteClicks = pickMetric(entities, ["website_clicks"]);
  const engagement = pickMetric(entities, ["engagement"]);

  const impMap = valuesToDayMap(impressions);
  const reachMap = valuesToDayMap(reach);
  const pvMap = valuesToDayMap(profileViews);
  const wcMap = valuesToDayMap(websiteClicks);
  const engMap = valuesToDayMap(engagement);

  const keys = _.uniq([
    ...Array.from(impMap.keys()),
    ...Array.from(reachMap.keys()),
    ...Array.from(pvMap.keys()),
    ...Array.from(wcMap.keys()),
    ...Array.from(engMap.keys()),
  ]).sort((a, b) => moment(a).valueOf() - moment(b).valueOf());

  if (!keys.length) return [];

  return keys.map((k) => {
    const impressionsVal = impMap.get(k) ?? 0;
    const reachVal = reachMap.get(k) ?? 0;
    const interactionsVal =
      engMap.get(k) ?? (pvMap.get(k) ?? 0) + (wcMap.get(k) ?? 0);
    return {
      label: formatAxisLabelEs(k),
      reach: reachVal,
      impressions: impressionsVal,
      interactions: interactionsVal,
      profileActivity: pvMap.get(k) ?? 0,
    };
  });
};

const sortedFollowerPoints = (
  m: Metric | undefined
): { k: string; v: number }[] => {
  if (!m?.values?.length) return [];
  const pts = m.values
    .map((v) => {
      const k = dateKey(v.label);
      return k ? { k, v: Number(v.value) || 0 } : null;
    })
    .filter(Boolean) as { k: string; v: number }[];
  pts.sort((a, b) => moment(a.k).valueOf() - moment(b.k).valueOf());
  return pts;
};

const buildFollowerBalanceDaily = (
  entities: Record<string, Metric>
): FollowerBalanceDay[] => {
  const fc = pickMetric(entities, ["follower_count"]);
  const pts = sortedFollowerPoints(fc);
  return pts.map(({ k, v }) => ({
    label: formatAxisLabelEs(k),
    net: Math.round(v * 100) / 100,
  }));
};

const netNewFollowers = (entities: Record<string, Metric>): number => {
  return Math.round(totalMetricValue(pickMetric(entities, ["follower_count"])));
};

const mapFeedToTopContent = (posts: InstagramPost[]): InstagramTopContent[] => {
  const mapped = posts.map((post, index) => {
    const ts = post.timestamp ? post.timestamp * 1000 : Date.now();
    const m = moment(ts).locale("es");
    const mediaType = Number(post.mediaType);
    const type: InstagramTopContent["type"] =
      mediaType === 8 ? "Carrusel" : mediaType === 2 ? "Reel" : "Post";
    const text = post.text?.trim() || "(Sin texto)";
    const views = Number(post.viewCount) || 0;
    const likes = Number(post.likeCount) || 0;
    const comments = Number(post.commentCount) || 0;
    const reach = Number(post.reach) || Number(post.viewCount) || 0;
    const saves = Number(post.saves) || 0;
    const shares = Number(post.shares) || 0;
    const interactions =
      Number(post.totalInteractions) || likes + comments + saves + shares;
    return {
      id: post.id || `post-${index}`,
      type,
      title: text.length > 120 ? `${text.slice(0, 117)}…` : text,
      date: m.format("D MMM YYYY"),
      time: m.format("HH:mm"),
      reach,
      views,
      interactions,
      likes,
      comments,
      saves,
      shares,
      engagementRate:
        reach > 0
          ? (100 * interactions) / reach
          : views > 0
          ? (100 * interactions) / views
          : 0,
      status: "Publicado" as const,
    };
  });
  return mapped.sort((a, b) => {
    if (b.interactions !== a.interactions) return b.interactions - a.interactions;
    if (b.engagementRate !== a.engagementRate) return b.engagementRate - a.engagementRate;
    return moment(b.date, "D MMM YYYY", "es").valueOf() - moment(a.date, "D MMM YYYY", "es").valueOf();
  });
};

const buildHashtagsFromPosts = (
  posts: InstagramTopContent[],
  feedPosts: InstagramPost[]
): InstagramHashtagRow[] => {
  const byId = new Map(posts.map((post) => [post.id, post]));
  const buckets = new Map<string, InstagramHashtagRow>();
  const hashtagRegex = /#[\p{L}\p{N}_]+/gu;

  for (const post of feedPosts) {
    const topPost = byId.get(post.id);
    if (!topPost) continue;
    const tags = _.uniq((post.text || "").match(hashtagRegex) || []).map((tag) =>
      tag.toLowerCase()
    );
    for (const hashtag of tags) {
      const current =
        buckets.get(hashtag) || {
          id: hashtag,
          hashtag,
          posts: 0,
          reach: 0,
          interactions: 0,
          engagementRate: 0,
        };
      current.posts += 1;
      current.reach += topPost.reach;
      current.interactions += topPost.interactions;
      current.engagementRate =
        current.reach > 0 ? (100 * current.interactions) / current.reach : 0;
      buckets.set(hashtag, current);
    }
  }

  return Array.from(buckets.values()).sort(
    (a, b) => b.interactions - a.interactions
  );
};

const filterPostsInRange = (
  posts: InstagramPost[],
  since: string,
  until: string
): InstagramPost[] => {
  const start = moment(since).startOf("day");
  const end = moment(until).endOf("day");
  if (!start.isValid() || !end.isValid()) return posts;
  return posts.filter((p) => {
    const ms = Number(p.timestamp) * 1000;
    if (!Number.isFinite(ms) || ms <= 0) return true;
    const t = moment(ms);
    if (!t.isValid()) return true;
    return t.isBetween(start, end, undefined, "[]");
  });
};

const daysInclusive = (since: string, until: string): number => {
  const d = moment(until).diff(moment(since), "days") + 1;
  return Math.max(1, d);
};

const compareVsPrevious = (current: number, previous: number) => {
  const safeCurrent = Number(current) || 0;
  const safePrev = Number(previous) || 0;
  if (safeCurrent > safePrev) {
    const change =
      safePrev > 0 ? Math.round((((safeCurrent - safePrev) / safePrev) * 1000)) / 10 : 0;
    return { direction: "up" as const, change };
  }
  if (safeCurrent < safePrev) {
    const change =
      safePrev > 0 ? Math.round((((safeCurrent - safePrev) / safePrev) * 1000)) / 10 : 0;
    return { direction: "down" as const, change };
  }
  return { direction: "flat" as const, change: 0 };
};

const withVariation = (
  item: InstagramStatItem,
  current: number,
  previous: number
): InstagramStatItem => {
  const { direction, change } = compareVsPrevious(current, previous);
  return {
    ...item,
    value: current,
    direction,
    change,
  };
};

function parseGenderAgeLabel(
  label: string
): { genderEs: string; ageKey: string } | null {
  const dot = label.indexOf(".");
  if (dot < 0) return null;
  const g = label.slice(0, dot).trim().toUpperCase();
  const ageKey = label.slice(dot + 1).trim();
  if (!ageKey) return null;
  let genderEs = "Desconocido";
  if (g === "F") genderEs = "Mujer";
  else if (g === "M") genderEs = "Hombre";
  else if (g === "U") genderEs = "Desconocido";
  return { genderEs, ageKey };
}

function aggregateAgeFromGenderAge(
  metric: Metric | undefined
): InstagramAudienceSegment[] {
  if (!metric?.values?.length) return [];
  const buckets = new Map<string, number>();
  for (const { label, value } of metric.values) {
    const parsed = parseGenderAgeLabel(label);
    if (!parsed) continue;
    const { ageKey } = parsed;
    buckets.set(ageKey, (buckets.get(ageKey) ?? 0) + (Number(value) || 0));
  }
  let sum = 0;
  buckets.forEach((v) => {
    sum += v;
  });
  if (!sum) sum = 1;
  const segments: InstagramAudienceSegment[] = [];
  buckets.forEach((value, label) => {
    segments.push({
      label,
      value,
      percentage: Math.round((value * 1000) / sum) / 10,
    });
  });
  return segments;
}

function aggregateGenderFromGenderAge(
  metric: Metric | undefined
): InstagramAudienceSegment[] {
  if (!metric?.values?.length) return [];
  const buckets = new Map<string, number>();
  for (const { label, value } of metric.values) {
    const parsed = parseGenderAgeLabel(label);
    if (!parsed) continue;
    const { genderEs } = parsed;
    buckets.set(
      genderEs,
      (buckets.get(genderEs) ?? 0) + (Number(value) || 0)
    );
  }
  let sum = 0;
  buckets.forEach((v) => {
    sum += v;
  });
  if (!sum) sum = 1;
  const segments: InstagramAudienceSegment[] = [];
  buckets.forEach((value, label) => {
    segments.push({
      label,
      value,
      percentage: Math.round((value * 1000) / sum) / 10,
    });
  });
  return segments;
}

const mergeGrowth = (
  base: InstagramAnalyticsSnapshot["growth"],
  entities: Record<string, Metric>,
  postsInPeriod: number
): InstagramStatItem[] => {
  const followers = pickMetric(entities, ["followers_count", "follower_count"]);
  const following = pickMetric(entities, ["follows_count", "following_count"]);
  const postsMetric = pickMetric(entities, ["media_count", "posts_count"]);

  const followersTotal = followers ? totalMetricValue(followers) : 0;
  const followingTotal = following ? totalMetricValue(following) : 0;
  const contentFromApi = postsMetric ? totalMetricValue(postsMetric) : 0;
  const contentTotal =
    contentFromApi > 0 ? Math.round(contentFromApi) : postsInPeriod;

  const newFollowers = netNewFollowers(entities);
  const followerBase = followersTotal - newFollowers;
  const followerChange =
    followerBase > 0
      ? Math.round((newFollowers * 1000) / followerBase) / 10
      : 0;

  const patchById: Record<string, Partial<InstagramStatItem>> = {
    followers: {
      value: Math.round(followersTotal),
      description:
        newFollowers !== 0
          ? `${newFollowers > 0 ? "+" : ""}${newFollowers} seguidores en el período`
          : followers?.description ?? "Seguidores informados por Instagram",
      change: followerChange,
      direction:
        followerChange > 0 ? "up" : followerChange < 0 ? "down" : "flat",
    },
    following: {
      value: Math.round(followingTotal),
      description: following?.description ?? "Meta no expone este dato en insights",
      change: 0,
      direction: "flat",
    },
    "total-content": {
      value: contentTotal,
      description: postsMetric?.description ?? base[2]?.description,
      change: 0,
      direction: "flat",
    },
    "new-followers": {
      value: newFollowers,
      description:
        newFollowers !== 0
          ? "Suma de follower_count en el período seleccionado"
          : base[3]?.description,
      change: 0,
      direction: "flat",
    },
  };

  return base.map((row) => ({
    ...row,
    ...patchById[row.id],
  }));
};

const buildOrganicSummary = (
  base: InstagramAnalyticsSnapshot["organicSummary"],
  entities: Record<string, Metric>,
  postsInPeriod: number,
  likes: number,
  comments: number,
  previous: {
    entities: Record<string, Metric>;
    postsInPeriod: number;
    likes: number;
    comments: number;
  }
): InstagramStatItem[] => {
  const reachSum = totalMetricValue(pickMetric(entities, ["reach"]));
  const impressionsSum = totalMetricValue(
    pickMetric(entities, ["impressions"])
  );
  const interactions = likes + comments;
  const denominator = postsInPeriod > 0 ? postsInPeriod : 1;
  const avgReachPost =
    postsInPeriod > 0 ? Math.round(reachSum / denominator) : 0;
  const engagementPct =
    reachSum > 0 ? Math.round((interactions * 1000) / reachSum) / 10 : 0;

  const previousReachSum = totalMetricValue(
    pickMetric(previous.entities, ["reach"])
  );
  const previousImpressions = totalMetricValue(
    pickMetric(previous.entities, ["impressions"])
  );
  const previousInteractions = previous.likes + previous.comments;
  const previousAvgReachPost =
    previous.postsInPeriod > 0
      ? Math.round(previousReachSum / previous.postsInPeriod)
      : 0;
  const previousEngagementPct =
    previousReachSum > 0
      ? Math.round((previousInteractions * 1000) / previousReachSum) / 10
      : 0;

  return [
    withVariation(base[0], engagementPct, previousEngagementPct),
    withVariation(base[1], interactions, previousInteractions),
    withVariation(base[2], avgReachPost, previousAvgReachPost),
    withVariation(base[3], Math.round(impressionsSum), Math.round(previousImpressions)),
    withVariation(base[4], postsInPeriod, previous.postsInPeriod),
  ];
};

const buildOrganicInteractions = (
  base: InstagramAnalyticsSnapshot["organicInteractions"],
  since: string,
  until: string,
  likes: number,
  comments: number,
  saves: number,
  postsInPeriod: number,
  previous: {
    since: string;
    until: string;
    likes: number;
    comments: number;
    saves: number;
    postsInPeriod: number;
  }
): InstagramStatItem[] => {
  const days = daysInclusive(since, until);
  const likesDay = Math.round((likes * 100) / days) / 100;
  const denomPost = postsInPeriod > 0 ? postsInPeriod : 1;
  const likesPost = Math.round((likes * 100) / denomPost) / 100;
  const commentsDay = Math.round((comments * 100) / days) / 100;
  const commentsPost = Math.round((comments * 100) / denomPost) / 100;
  const likesComment =
    comments > 0 ? Math.round((likes * 100) / comments) / 100 : 0;

  const previousDays = daysInclusive(previous.since, previous.until);
  const previousLikesDay = Math.round((previous.likes * 100) / previousDays) / 100;
  const previousDenomPost = previous.postsInPeriod > 0 ? previous.postsInPeriod : 1;
  const previousLikesPost =
    Math.round((previous.likes * 100) / previousDenomPost) / 100;
  const previousCommentsDay =
    Math.round((previous.comments * 100) / previousDays) / 100;
  const previousCommentsPost =
    Math.round((previous.comments * 100) / previousDenomPost) / 100;
  const previousLikesComment =
    previous.comments > 0
      ? Math.round((previous.likes * 100) / previous.comments) / 100
      : 0;

  return [
    withVariation(base[0], likes, previous.likes),
    withVariation(base[1], comments, previous.comments),
    withVariation(base[2], saves, previous.saves),
    withVariation(base[3], postsInPeriod, previous.postsInPeriod),
    withVariation(base[4], likesDay, previousLikesDay),
    withVariation(base[5], likesPost, previousLikesPost),
    withVariation(base[6], commentsDay, previousCommentsDay),
    withVariation(base[7], commentsPost, previousCommentsPost),
    withVariation(base[8], likesComment, previousLikesComment),
  ];
};

const buildFollowerBalanceKpis = (
  base: InstagramAnalyticsSnapshot["followerBalance"],
  daily: FollowerBalanceDay[],
  since: string,
  until: string,
  postsInPeriod: number
): InstagramStatItem[] => {
  const days = daysInclusive(since, until);
  if (!daily.length) {
    const postsPerDay = Math.round((postsInPeriod * 100) / days) / 100;
    const postsWeek = Math.round(postsPerDay * 7 * 100) / 100;
    return base.map((item) => ({
      ...item,
      value:
        item.id === "posts-day"
          ? postsPerDay
          : item.id === "posts-week"
          ? postsWeek
          : 0,
      direction: "flat",
    }));
  }
  const avgNet =
    Math.round((daily.reduce((a, d) => a + d.net, 0) * 100) / daily.length) /
    100;
  const followersPerPost =
    postsInPeriod > 0
      ? Math.round((avgNet * 100 * days) / postsInPeriod) / 100
      : base[1].value;
  const postsPerDay = Math.round((postsInPeriod * 100) / days) / 100;
  const postsWeek = Math.round(postsPerDay * 7 * 100) / 100;

  return [
    { ...base[0], value: avgNet, description: base[0].description },
    { ...base[1], value: followersPerPost, description: base[1].description },
    { ...base[2], value: base[2].value },
    { ...base[3], value: postsPerDay, description: base[3].description },
    { ...base[4], value: postsWeek, description: base[4].description },
  ];
};

const mergeSummaryCard = (
  base: InstagramAnalyticsSnapshot["summary"],
  entities: Record<string, Metric>,
  postsInPeriod: number
): InstagramAnalyticsSnapshot["summary"] => {
  const reach = totalMetricValue(pickMetric(entities, ["reach"]));
  const impressions = totalMetricValue(
    pickMetric(entities, ["impressions"])
  );
  const profileViews = totalMetricValue(
    pickMetric(entities, ["profile_views"])
  );
  const websiteClicks = totalMetricValue(
    pickMetric(entities, ["website_clicks"])
  );
  const engagementTotal = totalMetricValue(
    pickMetric(entities, ["engagement"])
  );
  const interactionAccounts = Math.round(
    engagementTotal > 0
      ? engagementTotal
      : profileViews + websiteClicks
  );

  return [
    {
      ...base[0],
      value: Math.round(reach),
      change: 0,
      direction: "flat",
    },
    {
      ...base[1],
      value: Math.round(impressions),
      change: 0,
      direction: "flat",
    },
    {
      ...base[2],
      value: interactionAccounts,
      change: 0,
      direction: "flat",
    },
    {
      ...base[3],
      value: postsInPeriod,
      change: 0,
      direction: "flat",
    },
  ];
};

export interface BuildInstagramSnapshotInput {
  insightsEntities: Record<string, Metric>;
  feedPosts: InstagramPost[];
  previousInsightsEntities?: Record<string, Metric>;
  previousFeedPosts?: InstagramPost[];
  accountName: string;
  communityName: string;
  periodLabel: string;
  updatedAt: string;
  since: string;
  until: string;
  previousSince?: string;
  previousUntil?: string;
}

export function buildInstagramAnalyticsSnapshotFromApi(
  input: BuildInstagramSnapshotInput
): InstagramAnalyticsSnapshot {
  const {
    insightsEntities: entities,
    feedPosts,
    previousInsightsEntities = {},
    previousFeedPosts = [],
    accountName,
    communityName,
    periodLabel,
    updatedAt,
    since,
    until,
    previousSince = since,
    previousUntil = until,
  } = input;

  const postsInRange = filterPostsInRange(feedPosts, since, until);
  const postsInPeriod = postsInRange.length;
  const likes = postsInRange.reduce((a, p) => a + (p.likeCount || 0), 0);
  const comments = postsInRange.reduce((a, p) => a + (p.commentCount || 0), 0);
  const saves = postsInRange.reduce((a, p) => a + (p.saves || 0), 0);
  const previousPostsInRange = filterPostsInRange(
    previousFeedPosts,
    previousSince,
    previousUntil
  );
  const previousPostsInPeriod = previousPostsInRange.length;
  const previousLikes = previousPostsInRange.reduce(
    (a, p) => a + (p.likeCount || 0),
    0
  );
  const previousComments = previousPostsInRange.reduce(
    (a, p) => a + (p.commentCount || 0),
    0
  );
  const previousSaves = previousPostsInRange.reduce((a, p) => a + (p.saves || 0), 0);

  const trend = buildTrend(entities);
  const countries = mapCountrySlices(
    pickMetric(entities, ["audience_country", "audience_locale"])
  );
  const cities = mapDemographicSlices(
    pickMetric(entities, ["audience_city"])
  ).slice(0, 12);
  const genderMetric = pickMetric(entities, ["audience_gender"]);
  const ageGenderMetric = pickMetric(entities, [
    "audience_gender_age",
    "audience_gender",
  ]);
  const ageGender = mapDemographicSlices(ageGenderMetric).slice(0, 12);
  const age = aggregateAgeFromGenderAge(ageGenderMetric);
  const gender =
    genderMetric && genderMetric.values?.length
      ? mapDemographicSlices(genderMetric)
      : aggregateGenderFromGenderAge(ageGenderMetric);

  const followerBalanceDaily = buildFollowerBalanceDaily(entities);
  const topContent =
    postsInRange.length > 0
      ? mapFeedToTopContent(postsInRange).slice(0, 40)
      : [];
  const hashtags = buildHashtagsFromPosts(topContent, postsInRange);

  const followerBalance = buildFollowerBalanceKpis(
    instagramAnalyticsMock.followerBalance,
    followerBalanceDaily,
    since,
    until,
    postsInPeriod
  );

  return {
    ...instagramAnalyticsMock,
    accountName,
    communityName,
    updatedAt,
    periodLabel,
    summary: mergeSummaryCard(
      instagramAnalyticsMock.summary,
      entities,
      postsInPeriod
    ),
    trend,
    followerBalanceDaily,
    growth: mergeGrowth(instagramAnalyticsMock.growth, entities, postsInPeriod),
    followerBalance,
    organicSummary: buildOrganicSummary(
      instagramAnalyticsMock.organicSummary,
      entities,
      postsInPeriod,
      likes,
      comments,
      {
        entities: previousInsightsEntities,
        postsInPeriod: previousPostsInPeriod,
        likes: previousLikes,
        comments: previousComments,
      }
    ),
    organicInteractions: buildOrganicInteractions(
      instagramAnalyticsMock.organicInteractions,
      since,
      until,
      likes,
      comments,
      saves,
      postsInPeriod,
      {
        since: previousSince,
        until: previousUntil,
        likes: previousLikes,
        comments: previousComments,
        saves: previousSaves,
        postsInPeriod: previousPostsInPeriod,
      }
    ),
    countries,
    cities,
    gender,
    age,
    ageGender,
    topContent,
    hashtags,
  };
}
